class FindMyPart extends HTMLElement {
  constructor() {
    super();

    this.state = {
      type: null,
      brand: null,
      watts: null,
      pin: null,
      model: null,
      products: [],
      step: 1,
    };

    this.batteryData = [];
    this.adapterData = [];
    
    // Trackers for background fetching
    this.isFetching = false;
    this.dataLoadedFully = false;
  }

  connectedCallback() {
      this.cache();
      this.bindEvents();
      
      // Start loading data immediately in the background
      this.initializeGlobalData(); 
  }

  cache() {
    this.overlay = this.querySelector('[data-overlay]');
    this.dialog = this.querySelector('.find-my-part__dialog');
    this.body = this.querySelector('[data-body]');
    this.titleElement = this.querySelector('[data-title]');
    this.progress = this.querySelector('[data-progress]');
    this.stepLabel = this.querySelector('[data-step-label]');
    this.backButton = this.querySelector('[data-back]');
    this.closeButton = this.querySelector('[data-close]');
  }

  async initializeGlobalData() {
    if (this.dataLoadedFully || this.isFetching) return;

    // 1. Check Session Storage and Cache Age
    const cachedBattery = sessionStorage.getItem('fmp_battery_data');
    const cachedAdapter = sessionStorage.getItem('fmp_adapter_data');
    const cacheTime = sessionStorage.getItem('fmp_cache_time');
    const isFullyLoaded = sessionStorage.getItem('fmp_data_fully_loaded') === 'true';
    
    // Check if cache exists AND is less than 10 minutes old (600,000 milliseconds)
    const isCacheValid = cacheTime && (Date.now() - parseInt(cacheTime) < 600000);

    if (cachedBattery && cachedAdapter && isCacheValid && isFullyLoaded) {
      this.batteryData = JSON.parse(cachedBattery);
      this.adapterData = JSON.parse(cachedAdapter);
      this.dataLoadedFully = true;
      
      window.dispatchEvent(new CustomEvent('fmp:dataLoaded', {
        detail: { batteryData: this.batteryData, adapterData: this.adapterData }
      }));
      return;
    }

    // 2. Load the first 1,000 items from the HTML DOM
    const batteryJson = this.querySelector("[data-battery-json]");
    const adapterJson = this.querySelector("[data-adapter-json]");

    this.batteryData = [];
    this.adapterData = [];

    if (batteryJson) {
      try { this.batteryData = JSON.parse(batteryJson.textContent); } catch(e){}
    }
    if (adapterJson) {
      try { this.adapterData = JSON.parse(adapterJson.textContent); } catch(e){}
    }

    // Save Page 1 to sessionStorage
    try {
      sessionStorage.setItem('fmp_battery_data', JSON.stringify(this.batteryData));
      sessionStorage.setItem('fmp_adapter_data', JSON.stringify(this.adapterData));
    } catch(e) {}

    window.dispatchEvent(new CustomEvent('fmp:dataLoaded', {
      detail: { batteryData: this.batteryData, adapterData: this.adapterData }
    }));

    // 3. Fetch paginated data
    await this.fetchRemainingData();
  }

  async fetchRemainingData() {
    if (this.dataLoadedFully || this.isFetching) return;
    this.isFetching = true;

    let batScript = this.querySelector('[data-battery-json]');
    let adpScript = this.querySelector('[data-adapter-json]');
    
    let nextUrl = (batScript && batScript.getAttribute('data-next-url')) || 
                  (adpScript && adpScript.getAttribute('data-next-url')) || null;

    while (nextUrl && nextUrl.trim() !== '') {
      try {
        // Yield to main thread
        await new Promise(resolve => setTimeout(resolve, 100));

        const response = await fetch(nextUrl);
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const nextBat = doc.querySelector('[data-battery-json]');
        const nextAdp = doc.querySelector('[data-adapter-json]');

        if (nextBat) {
          try { this.batteryData.push(...JSON.parse(nextBat.textContent)); } catch(e){}
        }
        if (nextAdp) {
          try { this.adapterData.push(...JSON.parse(nextAdp.textContent)); } catch(e){}
        }

        try {
          sessionStorage.setItem('fmp_battery_data', JSON.stringify(this.batteryData));
          sessionStorage.setItem('fmp_adapter_data', JSON.stringify(this.adapterData));
          sessionStorage.setItem('fmp_cache_time', Date.now().toString());
        } catch(e) {
          console.warn('Session storage full, skipping cache.');
        }

        window.dispatchEvent(new CustomEvent('fmp:dataLoaded', {
          detail: { batteryData: this.batteryData, adapterData: this.adapterData }
        }));

        nextUrl = (nextBat && nextBat.getAttribute('data-next-url')) || 
                  (nextAdp && nextAdp.getAttribute('data-next-url')) || null;
      } catch (error) {
        console.error('Pagination fetch error:', error);
        nextUrl = null;
      }
    }

    this.dataLoadedFully = true;
    this.isFetching = false;
    
    try {
      sessionStorage.setItem('fmp_data_fully_loaded', 'true');
    } catch(e) {}

    if (this.state.step === 2) {
      this.renderBrandScreen();
    }
  }

  bindEvents() {
    this.boundHandleClick = this.handleDocumentClick.bind(this);
    document.addEventListener("click", this.boundHandleClick);
    this.overlay.addEventListener("click", () => this.close());
    this.closeButton.addEventListener("click", () => this.close());
    this.backButton.addEventListener("click", () => this.previous());
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.close();
    });
  }
  
  disconnectedCallback() {
    document.removeEventListener("click", this.boundHandleClick);
  }

  handleDocumentClick(event) {
    const trigger = event.target.closest("[data-find-my-part-open]");
    if (!trigger) return;
    event.preventDefault();

    this.open({
      type: trigger.dataset.type || null,
      brand: trigger.dataset.brand || null,
      watts: trigger.dataset.watts || null,
      pin: trigger.dataset.pin || null,
      model: trigger.dataset.model || null,
    });
  }

  open(data = {}) {
    this.reset();
    this.classList.add("is-open");
    document.body.classList.add("overflow-hidden");

    this.state.type = data.type;
    this.state.brand = data.brand;
    this.state.watts = data.watts;
    this.state.pin = data.pin;
    this.state.model = data.model;

    if (this.state.type) {
      this.startFinder();
    } else {
      this.renderTypeScreen();
    }
  }

  close() {
    this.classList.remove("is-open");
    document.body.classList.remove("overflow-hidden");
    this.reset();
  }

  reset() {
    this.state = {
      type: null,
      brand: null,
      watts: null,
      pin: null,
      model: null,
      products: [],
      step: 1,
    };
    this.body.innerHTML = "";
    this.progress.hidden = true;
    this.backButton.hidden = true;
    this.titleElement.textContent = "Find The Right Product";
  }

  startFinder() {
    this.state.step = 2;
    this.renderBrandScreen();
  }

  renderTypeScreen() {
    this.progress.hidden = true;
    this.backButton.hidden = true;
    this.titleElement.textContent = "Select Device";

    this.body.innerHTML = `
      <div class="find-my-part__types">
        <button class="find-my-part__type" data-type="battery">
          <span class="find-my-part__type-icon">🔋</span>
          <span class="find-my-part__type-text">Laptop Battery</span>
        </button>
        <button class="find-my-part__type" data-type="adapter">
          <span class="find-my-part__type-icon">🔌</span>
          <span class="find-my-part__type-text">Laptop Adapter</span>
        </button>
      </div>
    `;

    this.body.querySelectorAll("[data-type]").forEach((button) => {
      button.addEventListener("click", () => {
        this.state.type = button.dataset.type;
        this.startFinder();
      });
    });
  }

  renderBrandScreen() {
    this.titleElement.textContent = "Select Brand";
    this.progress.hidden = false;
    this.backButton.hidden = false;

    // Adapters now skip model, so max 5 steps
    const totalSteps = this.state.type === "battery" ? 4 : 5;
    this.updateProgress(this.state.step, totalSteps);

    const source = this.state.type === "battery" ? this.batteryData : this.adapterData;
    const brands = [...new Set(source.map((item) => item.brand))].filter(Boolean).sort();

    this.renderBrandList(brands);

    if (this.state.brand && brands.includes(this.state.brand)) {
      this.selectBrand(this.state.brand);
    }
  }

  renderBrandList(brands) {
    const items = brands.map(brand => `
      <button type="button" class="find-my-part__list-item" data-brand="${brand}">
        ${brand}
      </button>
    `).join("");

    this.body.innerHTML = `
      <div class="find-my-part__search">
        <input type="text" class="find-my-part__search-input" placeholder="Search Brand" data-brand-search>
      </div>
      <div class="find-my-part__list" data-brand-list>${items}</div>
    `;

    this.body.querySelectorAll("[data-brand]").forEach((button) => {
      button.addEventListener("click", () => this.selectBrand(button.dataset.brand));
    });

    const input = this.body.querySelector("[data-brand-search]");
    input.addEventListener("input", () => this.filterBrandList(input.value));
  }

  filterBrandList(keyword) {
    keyword = keyword.trim().toLowerCase();
    this.body.querySelectorAll("[data-brand]").forEach((item) => {
      item.hidden = !item.textContent.toLowerCase().includes(keyword);
    });
  }

  selectBrand(brand) {
    this.state.brand = brand;
    this.state.step = 3;
    
    if (this.state.type === 'battery') {
      this.renderModelScreen(); 
    } else {
      // Adapter: evaluate watts immediately
      this.evaluateNextAdapterStep('brand');
    }
  }

  updateProgress(current, total) {
    this.stepLabel.textContent = `Step ${current} of ${total}`;
    const dots = this.progress.querySelectorAll(".find-my-part__dots span");
    
    dots.forEach((dot, index) => {
      const stepNum = index + 1;
      
      dot.classList.remove("is-completed", "is-current", "is-upcoming");
      
      if (stepNum < current) {
        dot.classList.add("is-completed");
      } else if (stepNum === current) {
        dot.classList.add("is-current");
      } else {
        dot.classList.add("is-upcoming");
      }
      
      dot.hidden = stepNum > total;
    });
  }

  // Model Screen is now only for Batteries
  renderModelScreen() {
    this.titleElement.textContent = "Select Model";
    this.updateProgress(this.state.step, 4); 

    let models = this.batteryData
      .filter((item) => item.brand === this.state.brand)
      .map((item) => item.model);

    models = [...new Set(models)].filter(Boolean).sort();

    this.body.innerHTML = `
      <div class="find-my-part__search">
        <input type="text" class="find-my-part__search-input" placeholder="Search Model" data-model-search>
      </div>
      <div class="find-my-part__list" data-model-list>
        ${models.map(model => `
          <button type="button" class="find-my-part__list-item" data-model="${model}">
            ${model}
          </button>
        `).join("")}
      </div>
    `;

    this.body.querySelectorAll("[data-model]").forEach((button) => {
      button.addEventListener("click", () => {
        this.state.model = button.dataset.model;
        this.state.step++;
        this.renderProducts();
      });
    });

    const input = this.body.querySelector("[data-model-search]");
    input.addEventListener("input", () => {
      const keyword = input.value.trim().toLowerCase();
      this.body.querySelectorAll("[data-model]").forEach((button) => {
        button.hidden = !button.textContent.toLowerCase().includes(keyword);
      });
    });
  }

  evaluateNextAdapterStep(fromStep) {
    // Adapter logic: ignores model, uses brand only as base
    const entries = this.adapterData.filter(i => i.brand === this.state.brand);

    if (fromStep === 'brand') {
      const watts = [...new Set(entries.map(i => i.watts))].filter(Boolean);
      
      if (watts.length > 0) {
        this.renderWattsScreen();
        return;
      } else {
        this.state.watts = null;
      }
      fromStep = 'watts'; 
    }

    if (fromStep === 'watts') {
      const pinEntries = entries.filter(i => !this.state.watts || i.watts === this.state.watts);
      const pins = [...new Set(pinEntries.map(i => i.pin))].filter(Boolean);

      if (pins.length > 0) { 
        this.state.step++;
        this.renderPinScreen();
        return;
      } else {
        this.state.pin = null;
      }
    }

    this.state.step++;
    this.renderProducts();
  }

  renderWattsScreen() {
    this.titleElement.textContent = "Select Watts";
    this.updateProgress(this.state.step, 5);

    const entries = this.adapterData.filter(i => i.brand === this.state.brand);
    const watts = [...new Set(entries.map((item) => item.watts))].filter(Boolean).sort();

    this.body.innerHTML = `
      <div class="find-my-part__search">
        <input type="text" class="find-my-part__search-input" placeholder="Search Watts" data-watts-search>
      </div>
      <div class="watts-information">
        Check your current charger or the laptop back panel for watt info (e.g., 19.5V x 3.34A = 65W)
      </div>
      <div class="find-my-part__list">
        ${watts.map(watt => `
          <button type="button" class="find-my-part__list-item" data-watts="${watt}">
            ${watt}
          </button>
        `).join("")}
      </div>
    `;

    this.body.querySelectorAll("[data-watts]").forEach((button) => {
      button.addEventListener("click", () => {
        this.state.watts = button.dataset.watts;
        this.evaluateNextAdapterStep('watts');
      });
    });

    const input = this.body.querySelector("[data-watts-search]");
    input.addEventListener("input", () => {
      const keyword = input.value.trim().toLowerCase();
      this.body.querySelectorAll("[data-watts]").forEach((button) => {
        button.hidden = !button.textContent.toLowerCase().includes(keyword);
      });
    });
  }

  renderPinScreen() {
    this.titleElement.textContent = "Pin Size";
    this.updateProgress(this.state.step, 5);

    const entries = this.adapterData.filter(i => i.brand === this.state.brand && (!this.state.watts || i.watts === this.state.watts));
    const pins = [...new Set(entries.map((item) => item.pin))].filter(Boolean).sort();

    this.body.innerHTML = `
      <div class="find-my-part__list">
        ${pins.map(pin => `
          <button type="button" class="find-my-part__list-item" data-pin="${pin}">
            ${pin}
          </button>
        `).join("")}
      </div>
      
      <div class="find-my-part__instructions" style="margin-top: 24px;">
        <h3 style="font-size: 16px; font-weight: 700; margin-bottom: 16px; color: #212121;">Product Matching Instructions</h3>
        <img src="https://cdn.shopify.com/s/files/1/0799/6529/0681/files/pin-information.png?v=1786427720" alt="Product Matching Instructions" style="max-width: 100%; height: auto; border-radius: 8px; display: block;">
      </div>
    `;

    this.body.querySelectorAll("[data-pin]").forEach((button) => {
      button.addEventListener("click", () => {
        this.state.pin = button.dataset.pin;
        this.evaluateNextAdapterStep('pin');
      });
    });
  }

  previous() {
    if (!this.state.type) return;

    const currentView = this.titleElement.textContent;

    // Battery Logic
    if (this.state.type === "battery") {
      if (currentView === "Compatible Products") {
        this.state.model = null;
        this.state.step = 3;
        this.renderModelScreen();
      } else if (currentView === "Select Model") {
        this.state.brand = null;
        this.state.step = 2;
        this.renderBrandScreen();
      } else if (currentView === "Select Brand") {
        this.state.type = null;
        this.state.step = 1;
        this.renderTypeScreen();
      }
      return;
    }

    // Adapter Logic
    if (currentView === "Compatible Products") {
      const entries = this.adapterData.filter(i => i.brand === this.state.brand);
      const watts = [...new Set(entries.map(i => i.watts))].filter(Boolean);
      const pins = [...new Set(entries.filter(i => !this.state.watts || i.watts === this.state.watts).map(i => i.pin))].filter(Boolean);

      if (pins.length > 0) {
        this.state.pin = null;
        this.state.step--;
        this.renderPinScreen();
      } else if (watts.length > 0) {
        this.state.watts = null;
        this.state.pin = null;
        this.state.step--;
        this.renderWattsScreen();
      } else {
        this.state.brand = null;
        this.state.watts = null;
        this.state.pin = null;
        this.state.step = 2;
        this.renderBrandScreen();
      }
    } else if (currentView === "Pin Size") {
      const entries = this.adapterData.filter(i => i.brand === this.state.brand);
      const watts = [...new Set(entries.map(i => i.watts))].filter(Boolean);
      if (watts.length > 0) {
        this.state.watts = null;
        this.state.step--;
        this.renderWattsScreen();
      } else {
        this.state.brand = null;
        this.state.watts = null;
        this.state.step = 2;
        this.renderBrandScreen();
      }
    } else if (currentView === "Select Watts") {
      this.state.brand = null;
      this.state.step = 2;
      this.renderBrandScreen();
    } else if (currentView === "Select Brand") {
      this.state.type = null;
      this.state.step = 1;
      this.renderTypeScreen();
    }
  }

  renderProducts() {
    this.titleElement.textContent = "Compatible Products";
    this.updateProgress(this.state.step, this.state.step);

    let finalProducts = [];

    if (this.state.type === "battery") {
      const record = this.batteryData.find(item => item.brand === this.state.brand && item.model === this.state.model);
      if (record) finalProducts = record.products;
    } else {
      // Find ALL entries matching the adapter criteria (ignoring model)
      const matchingEntries = this.adapterData.filter((item) => {
        let match = item.brand === this.state.brand;
        if (this.state.watts) match = match && item.watts === this.state.watts;
        if (this.state.pin) match = match && item.pin === this.state.pin;
        return match;
      });

      // Combine products from all matching entries
      let combinedProducts = [];
      matchingEntries.forEach(entry => {
        if (entry.products) combinedProducts.push(...entry.products);
      });

      // Deduplicate products by their unique ID
      finalProducts = Array.from(new Map(combinedProducts.map(p => [p.id, p])).values());
    }

    if (!finalProducts.length) {
      this.body.innerHTML = `
        <div class="find-my-part__empty">
          <h3>No Products Found</h3>
          <p>Please try another configuration.</p>
        </div>
      `;
      return;
    }

    if (finalProducts.length === 1) {
      window.open(finalProducts[0].url, '_blank');
      return;
    }


    this.body.innerHTML = `
      <div class="find-my-part__products">
        ${finalProducts.map(product => `
          <a href="${product.url}" target="_blank" class="find-my-part__product">
            <div class="find-my-part__product-image">
              <img src="${product.image}" alt="${product.title}" loading="lazy">
            </div>
            <div class="find-my-part__product-content">
              <h3>${product.title}</h3>
              <div class="find-my-part__product-price">${product.price}</div>
              <span class="find-my-part__product-button">View Product</span>
            </div>
          </a>
        `).join("")}
      </div>
    `;
  }
}

customElements.define("find-my-part", FindMyPart);
